import React, { useState, useEffect } from "react";
import {
  Card,
  Select,
  Button,
  Input,
  Space,
  Typography,
  Divider,
  DatePicker,
  message
} from "antd";
import { validateStage } from "./validation";
import moment from "moment";

const { Text } = Typography;
const customFieldOption = { label: "<Custom field...>", value: "__custom_field__" };



// NOTE: Extended StageInspector with advanced lookup matching support.
// - In advanced mode pipeline match rules you can use either a literal
//   value or pick a local (main object) field as the match value.

function StageInspector({
  selectedStage,
  fields,
  onUpdate,
  pipeline,
  mtObjects,
  ApiClient,
  loadFields
}) {
  const [cfg, setCfg] = useState({});
  const fieldsCache = React.useRef({});
  

  useEffect(() => {
    // Normalize older configs to the new shape for UI convenience
    const initial = selectedStage?.config || {};

    const normalized = { ...initial };

    // Backwards compat: if `groupBy` (string) exists, map to groupFields
    if (initial.groupBy && !initial.groupFields) {
      if (Array.isArray(initial.groupBy)) {
        normalized.groupFields = initial.groupBy.map((f) => ({ field: f }));
      } else if (typeof initial.groupBy === "string") {
        normalized.groupFields = [{ field: initial.groupBy }];
      }
    }

    // Backwards compat: single aggregate -> aggregates array
    if ((initial.aggregateField || initial.aggregateType) && !initial.aggregates) {
      normalized.aggregates = [
        {
          alias:
            initial.aggregateAs ||
            (initial.aggregateField ? `${initial.aggregateType || "agg"}_${initial.aggregateField}` : "agg"),
          field: initial.aggregateField || null,
          type: initial.aggregateType || "sum"
        }
      ];
    }

    // Defaults for $lookup/$join stage
    if (selectedStage?.type === "$join") {
      normalized.from = initial.from || (mtObjects && mtObjects[0] ? mtObjects[0].name : null);
      normalized.localField = initial.localField || null;
      normalized.foreignField = initial.foreignField || null;
      normalized.as = initial.as || "";
      normalized.pipeline = Array.isArray(initial.pipeline) ? initial.pipeline : [];
      normalized.fromFields = initial.fromFields || [];
      normalized.mode = initial.mode || "simple";
      // :key: Load fields on initial load / edit
      if (normalized.from) {
        loadFromFields(normalized.from);
      }
    }

    setCfg(normalized);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStage]);

  if (!selectedStage) return <Card size="small" style={{ width: 360 }}>Select a stage</Card>;

  const fieldOptions = fields.map((f) => ({
    label: `${f.label} (${f.name})`,
    value: f.name
  }));

  // previous group aliases
  // previous group aliases + join fields
    let previousGroupAliases = [];
    let previousJoinFields = [];

    let previousJoinProjectFields = [];

    if (pipeline?.length) {
      for (const st of pipeline) {
        if (st.id === selectedStage.id) break; // stop at current stage

        if (
          st.type === "$join" &&
          Array.isArray(st.config?.fromFields) &&
          st.config.fromFields.length > 0
        ) {
          const alias =
            st.config.lookupReturnType === "merge"
              ? st.config.from          // merge → object name
              : st.config.as;           // array/single → alias

          if (!alias) continue;

          previousJoinProjectFields.push(
            ...st.config.fromFields.map((f) => ({
              label: `${alias}.${f}`,
              value: `${alias}.${f}`
            }))
          );
        }
      }
    }


    if (pipeline?.length) {
      for (const st of pipeline) {
        if (st.id === selectedStage.id) break; // stop at current stage

        // 1️⃣ Collect group aliases
        if (st.type === "$group" && st.config?.aggregates) {
          previousGroupAliases = previousGroupAliases.concat(
            st.config.aggregates
              .map((a) => a.as || a.alias)
              .filter(Boolean)
          );
        }

        // 2️⃣ Collect join fromFields
        if (
          st.type === "$join" &&
          Array.isArray(st.config?.fromFields)
        ) {
          // lookupReturnType = "merge" → use `from` as alias
          const joinAlias =
            st.config.lookupReturnType === "merge"
              ? st.config.from
              : st.config.as;

          previousJoinFields = previousJoinFields.concat(
            st.config.fromFields.map((field) => ({
              label: `${joinAlias}.${field}`,
              value: `${joinAlias}.${field}`
            }))
          );
        }
      }
    }

    // 3️⃣ Final options
    const aggregateFieldOptions = [
      ...previousGroupAliases.map((alias) => ({
        label: alias,
        value: alias
      })),
      ...previousJoinFields
    ];
  const update = (p) => setCfg({ ...cfg, ...p });

  const normalizeType = (t) => {
    if (!t) return "";
    return String(t).toLowerCase();
  };

  const extractPicklistValues = (field) => {
    if (!field || !field.raw) return null;
    const r = field.raw;
    return (
      r.picklistValues ||
      r.picklist_values ||
      r.values ||
      r.options ||
      (r.meta && (r.meta.picklistValues || r.meta.values)) ||
      null
    );
  };

  const getValueComponent = (fieldType, value, updateFn, fieldMeta) => {
    const t = normalizeType(fieldType);

    const picklistVals = extractPicklistValues(fieldMeta);
    if (picklistVals && Array.isArray(picklistVals) && picklistVals.length > 0) {
      const opts = picklistVals.map((v) =>
        typeof v === "object"
          ? { label: v.label || v.value || v, value: v.value ?? v.label ?? v }
          : { label: String(v), value: v }
      );

      return (
        <Select
          value={value}
          onChange={(v) => updateFn({ value: v })}
          options={opts}
          style={{ width: "100%" }}
        />
      );
    }

    if (t === "boolean" || t === "bool") {
      return (
        <Select
          value={value === true || value === "true" ? true : value === false || value === "false" ? false : undefined}
          onChange={(v) => updateFn({ value: v })}
          options={[
            { label: "True", value: true },
            { label: "False", value: false }
          ]}
          style={{ width: "100%" }}
        />
      );
    }

    if (["number", "integer", "float", "double", "currency"].includes(t)) {
      return (
        <Input
          type="number"
          value={value === undefined || value === null ? "" : value}
          onChange={(e) => {
            const v = e.target.value;
            updateFn({ value: v === "" ? "" : Number(v) });
          }}
        />
      );
    }

    if (t === "date" || t === "datetime" || t === "timestamp") {
      return (
        <DatePicker
          style={{ width: "100%" }}
          value={value ? moment(value) : null}
          onChange={(date, dateString) => updateFn({ value: dateString })}
        />
      );
    }

    return (
      <Input
        value={value === undefined || value === null ? "" : value}
        onChange={(e) => updateFn({ value: e.target.value })}
      />
    );
  };

  const loadFromFields = async (objectName) => {
    if (!objectName) return [];
  
    // 1. return cached fields if exist
    if (fieldsCache.current[objectName]) {
      return fieldsCache.current[objectName];
    }
  
    // 2. OTHERWISE fetch fields and cache them
    const ff = await ApiClient.listMtFields(objectName);
    fieldsCache.current[objectName] = ff;   // cache it
  
    return ff;
  };

  const apply = () => {
    const err = validateStage({ ...selectedStage, config: cfg }, fields, pipeline);
    if (err) return message.error(err);
    onUpdate({ ...selectedStage, config: cfg });
    message.success("Updated");
  };

  // -------------------------
  // Helper to update nested pipeline stage rules immutably
  const updatePipelineRule = (stageIndex, ruleIndex, patch) => {
    const p = JSON.parse(JSON.stringify(cfg.pipeline || []));
    p[stageIndex] = p[stageIndex] || { type: "$match", rules: [], mode: "and" };
    p[stageIndex].rules = p[stageIndex].rules || [];
    p[stageIndex].rules[ruleIndex] = { ...(p[stageIndex].rules[ruleIndex] || {}), ...patch };
    setCfg({ ...cfg, pipeline: p });
  };

  // -------------------------
  // Render
  return (
    <Card title={selectedStage.type} size="small" style={{ width: 360, overflowY: "auto", height: "90vh" }}>
      {/* $lookup / $join */}
      {selectedStage.type === "$join" && (
        <Space direction="vertical" style={{ width: "100%" }}>
          {/*<Text strong>Lookup Mode</Text>
          <Select
            value={cfg.mode || "simple"}
            style={{ width: "100%" }}
            onChange={(v) => update({ mode: v })}
            options={[
              { label: "Simple Lookup", value: "simple" },
              { label: "Advanced Lookup", value: "advanced" }
            ]}
          />
          */}

          {/* SIMPLE */}
          {(!cfg.mode || cfg.mode === "simple") && (
            <Card size="small" style={{ background: "#fafafa" }}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong>From Object</Text>
                <Select
                  style={{ width: "100%" }}
                  value={cfg.from}
                  placeholder="Pick related object"
                  onChange={async (v) => {
                    const fields = await loadFromFields(v);
                  
                    update({
                      from: String(v),
                      foreignField: null,
                      pipeline: []
                    });
                  }}
                  options={mtObjects}
                />

                <Text strong>Local Field</Text>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Pick local field"
                  value={cfg.localField}
                  options={fieldOptions}
                  onChange={(v) => update({ localField: v })}
                />

                <Text strong>Foreign Field</Text>
                <Select
                  style={{ width: "100%" }}
                  placeholder="Pick foreign field"
                  value={cfg.foreignField}
                  options={ fieldsCache.current[cfg.from]?.map(f => ({ label: f.label || f.name, value: f.name })) || [] }
                  onChange={(v) => update({ foreignField: v })}
                />

                <Text strong>Return Type</Text>
                <Select
                  style={{ width: "100%" }}
                  value={cfg.lookupReturnType}
                  onChange={(v) => update({ lookupReturnType: v })}
                  options={[
                    { label: "Child Array (Default)", value: "array" },
                    { label: "Join As Single Record", value: "single" },
                    { label: "Merge Into Parent", value: "merge" }
                  ]}
                />
                <Text strong>Select Fields to Bring</Text>
                <Select
                  mode="multiple"
                  style={{ width: "100%" }}
                  placeholder="Pick fields from joined object"
                  value={cfg.fromFields}
                  onChange={(v) => update({ fromFields: v })}
                  options={
                    fieldsCache.current[cfg.from]?.map(f => ({
                      label: `${f.label || f.name}`,
                      value: f.name
                    })) || []
                  }
                />
                <Text strong>Output As</Text>
                <Input
                  placeholder="e.g. userInfo"
                  value={cfg.as}
                  onChange={(e) => update({ as: e.target.value })}
                />
              </Space>
            </Card>
          )}

       
          {cfg.mode === "simple" && (
            <Card size="small" style={{ background: "#f0f5ff" }}>
              <Space direction="vertical" style={{ width: "100%" }}>
                

                <Text strong>Advanced Match Conditions</Text>
                <Text type="secondary">Conditions inside the lookup pipeline</Text>

                <Button
                  type="dashed"
                  block
                  onClick={() => {
                    const pipelineArr = [...(cfg.pipeline || [])];
                    pipelineArr.push({ type: "$match", rules: [], mode: "and" , objectName: cfg.from });
                    update({ pipeline: pipelineArr });
                  }}
                >
                  + Add Lookup Match Stage
                </Button>
                

                {(cfg.pipeline || []).map((stg, idx) => (
                  <Card key={idx} size="small" style={{ background: "#fafafa", marginTop: 8 }}>
                    <Space direction="vertical" style={{ width: "100%" }}>
                      <Text strong>Stage #{idx + 1} — {stg.type || "$match"}</Text>

                      {/* rules */}
                      {(stg.rules || []).map((r, rIndex) => {
                        // ensure shape
                        const rule = r || {};
                        const fieldMeta = fieldsCache.current?.[cfg.from]?.find?.( (f) => f.name === rule.field ) || null;
                      
                        return (
                          <Card key={rIndex} size="small" style={{ marginTop: 8 }}>
                            <Space direction="vertical" style={{ width: "100%" }}>
                              <Text strong>Condition #{rIndex + 1}</Text>

                              {/* Field (from 'from' object) */}
                              <Select
                                placeholder="Field (from object)"
                                style={{ width: "100%" }}
                                value={rule.field}
                                options={ fieldsCache.current[cfg.from]?.map(f => ({ label: f.label || f.name, value: f.name })) || [] }
                                onChange={(v) => updatePipelineRule(idx, rIndex, { field: v })}
                              />

                              {/* Operator */}
                              <Select
                                placeholder="Operator"
                                style={{ width: "100%" }}
                                value={rule.operator}
                                options={[
                                  { label: "=", value: "eq" },
                                  { label: "!=", value: "ne" },
                                  { label: ">", value: "gt" },
                                  { label: "<", value: "lt" },
                                  { label: ">=", value: "gte" },
                                  { label: "<=", value: "lte" },
                                  { label: "in", value: "in" },
                                  { label: "not in", value: "nin" },
                                  { label: "contains", value: "contains" }
                                ]}
                                onChange={(v) => updatePipelineRule(idx, rIndex, { operator: v })}
                              />

                              {/* Value type: literal or localField (main object field) */}
                              <Text>Value Type</Text>
                              <Select
                                style={{ width: "100%" }}
                                value={rule.valueType || "literal"}
                                options={[
                                  { label: "Literal value", value: "literal" },
                                  { label: "Use main object field", value: "localField" }
                                ]}
                                onChange={(v) => {
                                  // reset value when switching types
                                  updatePipelineRule(idx, rIndex, { valueType: v, value: null, valueField: null });
                                }}
                              />

                              {/* If user wants to pick a local (main object) field as value */}
                              {rule.valueType === "localField" ? (
                                <>
                                  <Text>Pick main object field</Text>
                                  <Select
                                    style={{ width: "100%" }}
                                    value={rule.valueField || undefined}
                                    options={fieldOptions}
                                    onChange={(v) => updatePipelineRule(idx, rIndex, { valueField: v })}
                                  />
                                  <Text type="secondary">This will compare the foreign (from) field against the chosen main-object field value (useful when foreign stores string id).</Text>
                                </>
                              ) : (
                                /* Literal value input */
                                <>
                                  <Text>Value</Text>
                                  

                                      {rule.operator !== "in" &&
                                        rule.operator !== "nin" &&
                                        rule.operator !== "between" && (
                                          <>
                                            {getValueComponent(
                                              fieldMeta?.type,
                                              cfg.pipeline?.[idx]?.rules?.[rIndex]?.value,
                                              (v) => updatePipelineRule(idx, rIndex, { value: v?.value }),
                                              fieldMeta
                                            )}
                                          </>
                                        )}
                                </>
                              )}

                              <Button
                                danger
                                onClick={() => {
                                  const pipelineArr = JSON.parse(JSON.stringify(cfg.pipeline || []));
                                  pipelineArr[idx] = pipelineArr[idx] || { type: "$match", rules: [] };
                                  pipelineArr[idx].rules = (pipelineArr[idx].rules || []).filter((_, i) => i !== rIndex);
                                  setCfg({ ...cfg, pipeline: pipelineArr });
                                }}
                              >
                                Remove Condition
                              </Button>
                            </Space>
                          </Card>
                        );
                      })}

                      <Button
                        type="dashed"
                        block
                        onClick={() => {
                          const pipelineArr = JSON.parse(JSON.stringify(cfg.pipeline || []));
                          pipelineArr[idx] = pipelineArr[idx] || { type: "$match", rules: [] };
                          pipelineArr[idx].rules.push({ field: "", operator: "eq", valueType: "literal", value: "" });
                          setCfg({ ...cfg, pipeline: pipelineArr });
                        }}
                      >
                        + Add Condition
                      </Button>

                      <Button
                        danger
                        style={{ marginTop: 8 }}
                        onClick={() => {
                          const pipelineArr = (cfg.pipeline || []).filter((_, i) => i !== idx);
                          update({ pipeline: pipelineArr });
                        }}
                      >
                        Remove Stage
                      </Button>
                    </Space>
                  </Card>
                ))}
              </Space>
            </Card>
          )}
        </Space>
      )}

      {/* $match (main) */}
      {(selectedStage.type === "$match" || selectedStage.type === "$aggmatch") && (
        <Space direction="vertical" style={{ width: "100%", overflowY: "auto", height: "75vh" }}>
          <Text strong>Match Mode</Text>
          <Select
            value={cfg.mode || "and"}
            style={{ width: "100%" }}
            onChange={(v) => update({ mode: v })}
            options={[
              { label: "Match ALL (AND)", value: "and" },
              { label: "Match ANY (OR)", value: "or" }
            ]}
          />

          <Button
            type="dashed"
            block
            onClick={() => {
              const rules = cfg.rules || [];
              rules.push({ field: "", operator: "", value: null });
              update({ rules });
            }}
          >
            + Add Condition
          </Button>

          {(cfg.rules || []).map((rule, idx) => {
            const fieldMeta = fields.find((f) => f.name === rule.field);
            if (rule.rules) return <div key={idx}>Nested groups UI comes here</div>;

            const ops = [
              { label: "=", value: "eq" },
              { label: "!=", value: "ne" },
              { label: ">", value: "gt" },
              { label: "<", value: "lt" },
              { label: ">=", value: "gte" },
              { label: "<=", value: "lte" },
              { label: "contains", value: "contains" },
              { label: "not contains", value: "not_contains" },
              { label: "starts with", value: "starts_with" },
              { label: "ends with", value: "ends_with" },
              { label: "in", value: "in" },
              { label: "not in", value: "nin" },
              { label: "between", value: "between" },
              { label: "exists", value: "exists" },
              { label: "custom JSON", value: "custom" }
            ];

            return (
              <Card key={idx} size="small" style={{ background: "#fafafa" }}>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Text strong>Condition #{idx + 1}</Text>

                  <Text>Field</Text>
                  <Select
                    style={{ width: "100%" }}
                    value={rule.field || undefined}
                    options={
                      selectedStage.type === "$aggmatch"
                        ? [...aggregateFieldOptions]
                        : [...fieldOptions, customFieldOption]
                    }
                    onChange={(v) => {
                      const rules = [...(cfg.rules || [])];
                      if (v === "__custom_field__") {
                        rules[idx] = { ...rules[idx], field: "" };
                        rules[idx].__custom = true;
                      } else {
                        rules[idx] = { ...rules[idx], field: v, __custom: false };
                      }
                      update({ rules });
                    }}
                  />

                  {(rule.__custom || (rule.field && !fields.find((f) => f.name === rule.field) && !aggregateFieldOptions.find((a) => a.value === rule.field))) && (
                    <>
                      <Text>Custom Field Name</Text>
                      <Input
                        placeholder="e.g. totalHours"
                        value={rule.field || ""}
                        onChange={(e) => {
                          const rules = [...(cfg.rules || [])];
                          rules[idx] = { ...rules[idx], field: e.target.value, __custom: true };
                          update({ rules });
                        }}
                      />
                    </>
                  )}

                  {rule.field && (
                    <>
                      <Text>Operator</Text>
                      <Select
                        style={{ width: "100%" }}
                        value={rule.operator}
                        options={ops}
                        onChange={(v) => {
                          const rules = [...(cfg.rules || [])];
                          rules[idx].operator = v;
                          rules[idx].value = null;
                          update({ rules });
                        }}
                      />
                    </>
                  )}

                  {rule.operator && rule.operator !== "exists" && rule.operator !== "custom" && (
                    <>
                      <Text>Value</Text>

                      {(rule.operator === "in" || rule.operator === "nin") && (
                        <Select
                          mode="multiple"
                          style={{ width: "100%" }}
                          value={rule.value || []}
                          options={fieldMeta?.raw?.picklistValues?.map((v) => ({
                            label: v.label || v.value || v,
                            value: v.value || v
                          }))}
                          onChange={(vals) => {
                            const rules = [...cfg.rules];
                            rules[idx].value = vals;
                            update({ rules });
                          }}
                        />
                      )}

                      {rule.operator === "between" && (
                        <Space>
                          <Input
                            placeholder="From"
                            type="number"
                            onChange={(e) => {
                              const rules = [...cfg.rules];
                              rules[idx].value = rules[idx].value || [];
                              rules[idx].value[0] = e.target.value;
                              update({ rules });
                            }}
                          />
                          <Input
                            placeholder="To"
                            type="number"
                            onChange={(e) => {
                              const rules = [...cfg.rules];
                              rules[idx].value = rules[idx].value || [];
                              rules[idx].value[1] = e.target.value;
                              update({ rules });
                            }}
                          />
                        </Space>
                      )}

                      {rule.operator !== "in" &&
                        rule.operator !== "nin" &&
                        rule.operator !== "between" &&
                        getValueComponent(
                          fieldMeta?.type,
                          rule.value,
                          (v) => {
                            const rules = [...cfg.rules];
                            rules[idx].value = v.value;
                            update({ rules });
                          },
                          fieldMeta
                        )}
                    </>
                  )}

                  {rule.operator === "exists" && (
                    <Select
                      value={rule.value}
                      onChange={(v) => {
                        const rules = [...cfg.rules];
                        rules[idx].value = v;
                        update({ rules });
                      }}
                      options={[
                        { label: "Exists", value: true },
                        { label: "Does Not Exist", value: false }
                      ]}
                      style={{ width: "100%" }}
                    />
                  )}

                  {rule.operator === "custom" && (
                    <Input.TextArea
                      rows={3}
                      placeholder='{"Hour": {"$gt": 10}}'
                      value={rule.value}
                      onChange={(e) => {
                        const rules = [...cfg.rules];
                        rules[idx].value = e.target.value;
                        update({ rules });
                      }}
                    />
                  )}

                  <Button danger size="small" onClick={() => {
                    const rules = (cfg.rules || []).filter((_, i) => i !== idx);
                    update({ rules });
                  }}>
                    Remove
                  </Button>
                </Space>
              </Card>
            );
          })}
        </Space>
      )}

      {/* $group UI */}
      {selectedStage.type === "$group" && (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text strong>Group By Fields</Text>
          <Text type="secondary">(You can add multiple group fields)</Text>

          <Button
            type="dashed"
            block
            onClick={() => update({ groupFields: [...(cfg.groupFields || []), { field: null }] })}
          >
            + Add Group Field
          </Button>

          {(cfg.groupFields || []).map((g, idx) => (
            <Card key={`group-${idx}`} size="small">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Select
                  style={{ width: "100%" }}
                  value={g.field}
                  options={[...fieldOptions,...previousJoinProjectFields]}
                  onChange={(v) => {
                    const groupFields = [...(cfg.groupFields || [])];
                    groupFields[idx] = { ...groupFields[idx], field: v };
                    update({ groupFields });
                  }}
                />

                <Button danger onClick={() => {
                  const groupFields = (cfg.groupFields || []).filter((_, i) => i !== idx);
                  update({ groupFields });
                }}>
                  Remove
                </Button>
              </Space>
            </Card>
          ))}

          <Divider />

          <Text strong>Aggregations</Text>
          <Text type="secondary">(alias = output field name, e.g. totalHours)</Text>

          <Button
            type="dashed"
            block
            onClick={() => update({ aggregates: [...(cfg.aggregates || []), { alias: "", field: null, type: "sum" }] })}
          >
            + Add Aggregation
          </Button>

          {(cfg.aggregates || []).map((agg, idx) => (
            <Card key={`agg-${idx}`} size="small" style={{ background: "#fafafa" }}>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text>Alias</Text>
                <Input
                  value={agg.alias}
                  onChange={(e) => {
                    const aggregates = [...(cfg.aggregates || [])];
                    aggregates[idx] = { ...aggregates[idx], alias: e.target.value, as: e.target.value };
                    update({ aggregates });
                  }}
                />

                <Text>Field</Text>
                <Select
                  value={agg.field}
                  style={{ width: "100%" }}
                  options={[...fieldOptions, ...previousJoinProjectFields]}
                  onChange={(v) => {
                    const aggregates = [...(cfg.aggregates || [])];
                    aggregates[idx] = { ...aggregates[idx], field: v };
                    update({ aggregates });
                  }}
                />

                <Text>Type</Text>
                <Select
                  value={agg.type}
                  style={{ width: "100%" }}
                  options={[
                    { label: "Sum", value: "sum" },
                    { label: "Count", value: "count" },
                    { label: "Avg", value: "avg" },
                    { label: "Min", value: "min" },
                    { label: "Max", value: "max" }
                  ]}
                  onChange={(v) => {
                    const aggregates = [...(cfg.aggregates || [])];
                    aggregates[idx] = { ...aggregates[idx], type: v };
                    update({ aggregates });
                  }}
                />

                <Button danger onClick={() => {
                  const aggregates = (cfg.aggregates || []).filter((_, i) => i !== idx);
                  update({ aggregates });
                }}>
                  Remove
                </Button>
              </Space>
            </Card>
          ))}
        </Space>
      )}

      {/* $project */}
      {selectedStage.type === "$project" && (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text>Include Fields</Text>
          <Select mode="multiple" style={{ width: "100%" }} 
            options={
              [...fieldOptions, ...previousJoinProjectFields]
            }
            
            value={cfg.include} onChange={(v) => update({ include: v })} />
        </Space>
      )}

      {/* $sort */}
      {selectedStage.type === "$sort" && (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Button type="dashed" block onClick={() => setCfg({ ...cfg, sortBy: [...(cfg.sortBy || []), { field: null, order: "asc" }] })}>
            + Add Sort Field
          </Button>

          {(cfg.sortBy || []).map((row, idx) => (
            <Card key={idx} size="small" style={{ background: "#fafafa" }}>
              <Text strong>Sort #{idx + 1}</Text>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text>Field</Text>
                <Select style={{ width: "100%" }} value={row.field} options={fieldOptions} onChange={(v) => {
                  const updated = [...(cfg.sortBy || [])];
                  updated[idx] = { ...updated[idx], field: v };
                  setCfg({ ...cfg, sortBy: updated });
                }} />

                <Text>Order</Text>
                <Select style={{ width: "100%" }} value={row.order} onChange={(v) => {
                  const updated = [...(cfg.sortBy || [])];
                  updated[idx] = { ...updated[idx], order: v };
                  setCfg({ ...cfg, sortBy: updated });
                }} options={[{ label: "Ascending", value: "asc" }, { label: "Descending", value: "desc" }]} />

                <Button danger size="small" onClick={() => {
                  const updated = (cfg.sortBy || []).filter((_, i) => i !== idx);
                  setCfg({ ...cfg, sortBy: updated });
                }}>
                  Remove
                </Button>
              </Space>
            </Card>
          ))}
        </Space>
      )}

      {/* $limit */}
      {selectedStage.type === "$limit" && (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text>Skip</Text>
          <Input type="number" value={cfg.skip === undefined || cfg.skip === null ? 0 : cfg.skip} onChange={(e) => setCfg({ ...cfg, skip: Number(e.target.value) })} />

          <Text>Limit</Text>
          <Input type="number" value={cfg.limit === undefined || cfg.limit === null ? 50 : cfg.limit} onChange={(e) => setCfg({ ...cfg, limit: Number(e.target.value) })} />
        </Space>
      )}

      <Divider />
      <Button type="primary" block onClick={apply}>
        Apply
      </Button>
    </Card>
  );
}

export default StageInspector;